import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { m } from "@recobra/i18n/messages";
import type {
  ListTasksResponse,
  Task,
  UpdateTaskInput,
  UpdateTaskFieldsInput,
} from "@recobra/schemas/task";

import { tasksApi } from "../../lib/api";
import { getErrorMessage } from "../../lib/api";
import { applyOptimisticUpdate } from "./taskOptimisticUpdate";

interface UseUpdateTaskMutationOptions {
  username?: string;
  contextType?: "case" | "client";
  contextId?: string;
  onSuccess?: (variables: UpdateTaskVariables) => void;
  onError?: (error: Error, variables: UpdateTaskVariables) => void;
  onSettled?: (variables: UpdateTaskVariables) => void;
}
interface UpdateTaskVariables {
  id: string;
  input: UpdateTaskInput;
  silent?: boolean;
  notesRevision?: number;
}
export type TaskProgressInput = Pick<
  UpdateTaskFieldsInput,
  "satisfyRequirements" | "waiveRequirements"
>;

export const useUpdateTaskMutation = (options: UseUpdateTaskMutationOptions = {}) => {
  const queryClient = useQueryClient();
  const { username, contextType, contextId, onSuccess, onError, onSettled } = options;

  const mutation = useMutation({
    meta: { suppressErrorToast: true },
    /** Runs this parent's PATCHes one at a time, so completing never overtakes a pending checklist patch. */
    scope: { id: `task-update-${contextId ?? "global"}` },
    mutationFn: ({ id, input }: UpdateTaskVariables) => tasksApi.updateTask(id, input),

    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey: ["tasks", id] });
      await queryClient.cancelQueries({ queryKey: ["tasks", "list"] });
      if (username) {
        await queryClient.cancelQueries({
          queryKey: ["tasks", "assigned", username],
        });
      }
      if (contextType && contextId) {
        await queryClient.cancelQueries({
          queryKey: ["tasks", contextType, contextId],
        });
      }

      const previousListData = queryClient.getQueryData<Task[]>(["tasks", "list"]);
      const previousDashboardData = username
        ? queryClient.getQueryData<Task[]>(["tasks", "assigned", username])
        : undefined;

      const previousContextData =
        contextType && contextId
          ? queryClient.getQueryData<ListTasksResponse>(["tasks", contextType, contextId])
          : undefined;
      const previousTaskData = queryClient.getQueryData<Task>(["tasks", id]);

      const patchItems = (old: { items: Task[] } | undefined) => {
        if (!old) return old;

        return {
          ...old,
          items: old.items.map((task) =>
            task.id === id ? applyOptimisticUpdate(task, input) : task,
          ),
        };
      };
      const patchList = (old: Task[] | undefined) =>
        old?.map((task) => (task.id === id ? applyOptimisticUpdate(task, input) : task));

      if ("transition" in input || input.status !== "done") {
        queryClient.setQueryData<Task>(["tasks", id], (task) =>
          task ? applyOptimisticUpdate(task, input) : task,
        );
        queryClient.setQueryData<Task[]>(["tasks", "list"], patchList);
        if (username) {
          queryClient.setQueryData<Task[]>(["tasks", "assigned", username], patchList);
        }
        if (contextType && contextId) {
          queryClient.setQueryData<ListTasksResponse>(
            ["tasks", contextType, contextId],
            patchItems,
          );
        }
      }

      return { previousTaskData, previousListData, previousDashboardData, previousContextData };
    },

    onError: (err, variables, context) => {
      if (context?.previousTaskData) {
        queryClient.setQueryData(["tasks", variables.id], context.previousTaskData);
      }
      if (context?.previousListData) {
        queryClient.setQueryData(["tasks", "list"], context.previousListData);
      }
      if (username && context?.previousDashboardData) {
        queryClient.setQueryData<Task[]>(
          ["tasks", "assigned", username],
          context.previousDashboardData,
        );
      }
      if (contextType && contextId && context?.previousContextData) {
        queryClient.setQueryData<ListTasksResponse>(
          ["tasks", contextType, contextId],
          context.previousContextData,
        );
      }
      if (
        !variables.silent ||
        "transition" in variables.input ||
        variables.input.notes === undefined
      ) {
        toast.error(m.task_update_error({ message: getErrorMessage(err) }));
      }
      onError?.(err, variables);
    },

    onSuccess: (task, variables) => {
      queryClient.setQueryData<Task>(["tasks", variables.id], (cached) =>
        keepSingleReadProjections(task, cached),
      );
      if (!variables.silent) toast.success(m.task_updated_success());
      onSuccess?.(variables);
    },

    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["tasks", variables.id] });
      void queryClient.invalidateQueries({ queryKey: ["tasks", "list"] });
      if (username)
        void queryClient.invalidateQueries({ queryKey: ["tasks", "assigned", username] });
      if (contextType && contextId) {
        void queryClient.invalidateQueries({ queryKey: ["tasks", contextType, contextId] });
      }
      onSettled?.(variables);
    },
  });

  return {
    ...mutation,
    /**
     * An edit the user is waiting on. Checklist progress applies optimistically
     * and stays out of it, so the modal keeps its controls while it saves.
     */
    isSubmitting: mutation.isPending && !mutation.variables?.silent,
    mutateProgress: (id: string, input: TaskProgressInput) =>
      mutation.mutate({ id, input, silent: true }),
  };
};

/** The update response omits what only the single-task read computes. */
const keepSingleReadProjections = (updated: Task, cached: Task | undefined): Task =>
  cached
    ? {
        ...updated,
        previousActivities: cached.previousActivities,
      }
    : updated;
